import { NgModule } from '@angular/core';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { ApolloClientOptions, ApolloLink, Observable } from '@apollo/client/core';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import Auth from '@aws-amplify/auth';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import * as Sentry from '@sentry/browser';
import { LoaderService } from '@shared/services/loader.service';
import { AppInjector } from '@app/app-injector.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CustomHeadersService } from '@auth/services/custom-headers.service';
import { IGNORE_OPERATION_FAILURES } from './shared/constants';
import { FormLoadingStateService } from './shared/services/form-loading-state.service';
import { ModalV2Service } from '@voyagu/radar-design-system';
import { AccessBannerModalComponent } from '@app/ui-v2/components/access-banner-modal/access-banner-modal.component';
import { EulaInterstitialModalComponent } from '@app/ui-v2/components/eula-interstitial-modal/eula-interstitial-modal.component';
import { EulaInterstitialService } from '@app/shared/services/eula-interstitial.service';
import type { GraphQLFormattedError } from 'graphql';
import { environment } from '../environments/environment';
import { createApolloCache } from './graphql-cache.config';

let accessBannerModalOpen = false;
let eulaInterstitialModalOpen = false;

const EULA_NOT_ACCEPTED = 'EULA_NOT_ACCEPTED';
const EULA_ALLOWLIST_OPERATIONS = ['CurrentEula', 'RecordEulaAcceptance'];
const UNAUTHENTICATED = 'UNAUTHENTICATED';
const ACCESS_DENIED_CODES = ['PERMISSION_DENIED', 'SYSTEM_ACCESS_DENIED'];
const ACCESS_DENIED_TEXT = "You don't have permission for this operation";

type GraphqlErrorClass = 'infrastructure' | 'auth' | 'eula' | 'access_denied' | 'unknown';

type ErrorExtensions = {
  code?: string;
  eulaDocumentId?: string;
};

type HttpResponseLike = {
  ok?: boolean;
  status?: number;
};

function errorExtensions(error: GraphQLFormattedError | undefined): ErrorExtensions | undefined {
  return error?.extensions as ErrorExtensions | undefined;
}

function networkResultErrors(
  networkError: Error | null | undefined,
): readonly GraphQLFormattedError[] | undefined {
  const result = (
    networkError as { result?: { errors?: readonly GraphQLFormattedError[] } } | undefined
  )?.result;
  return result?.errors;
}

/** `graphQLErrors` on an HTTP error are the same list Apollo copied off `networkError.result`. */
function collectedErrors(
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  networkError: Error | null | undefined,
): readonly GraphQLFormattedError[] {
  if (graphQLErrors?.length) {
    return graphQLErrors;
  }
  return networkResultErrors(networkError) ?? [];
}

function firstErrorCode(
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  networkError: Error | null | undefined,
): string | undefined {
  return errorExtensions(collectedErrors(graphQLErrors, networkError)[0])?.code;
}

function httpOk(response: HttpResponseLike | undefined): boolean {
  if (!response) {
    return false;
  }
  if (typeof response.ok === 'boolean') {
    return response.ok;
  }
  return typeof response.status === 'number' && response.status >= 200 && response.status < 300;
}

/**
 * Class of a failed operation, from `errors[0].extensions.code` only.
 *
 * No code on a non-2xx response, a network drop, or a non-JSON body means the request never
 * reached Radar. Any other code is a domain error and stays on the existing masked-message path —
 * a class name is not a permission denial.
 */
function classifyGraphqlError(
  code: string | undefined,
  response: HttpResponseLike | undefined,
  networkError: Error | null | undefined,
): GraphqlErrorClass {
  if (code === UNAUTHENTICATED) {
    return 'auth';
  }
  if (code === EULA_NOT_ACCEPTED) {
    return 'eula';
  }
  if (code && ACCESS_DENIED_CODES.includes(code)) {
    return 'access_denied';
  }
  if (code) {
    return 'unknown';
  }
  if (networkError || (response && !httpOk(response))) {
    return 'infrastructure';
  }
  return 'unknown';
}

function eulaDocumentIdFromErrors(errors: readonly GraphQLFormattedError[]) {
  for (const error of errors) {
    const documentId = errorExtensions(error)?.eulaDocumentId;
    if (documentId) {
      return documentId;
    }
  }
  return null;
}

/**
 * Every Sentry call this middleware makes goes through one object, so a spec can watch what a
 * failed operation did and did not report. `import * as Sentry` is a frozen module namespace: a
 * spy cannot replace a function on it.
 */
export const sentryReporter = {
  setContext: (name: string, context: Record<string, unknown> | null) =>
    Sentry.setContext(name, context),
  captureMessage: (message: string, captureContext?: Parameters<typeof Sentry.captureMessage>[1]) =>
    Sentry.captureMessage(message, captureContext),
  addBreadcrumb: (breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0]) =>
    Sentry.addBreadcrumb(breadcrumb),
};

export const createErrorMiddleware = (authService: CognitoAuthService) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    const toastrService = AppInjector.injector.get(ToastrService);
    const formLoadingStateService = AppInjector.injector.get(FormLoadingStateService);
    const response = operation.getContext().response as HttpResponseLike | undefined;
    const code = firstErrorCode(graphQLErrors, networkError);
    const errorClass = classifyGraphqlError(code, response, networkError);
    const inlineErrorUi = operation.getContext().graphqlErrorUi === 'inline';
    const screenOwnsErrorUi = operation.getContext().skipGraphqlErrorUi === true;

    // Every class is logged with the code and the operation, so a WARP drop (no code) and a real
    // permission denial are separate counts. WARP and permission denials are Sentry events even
    // when the screen owns the rest of the error UI. A masked domain failure on an inline surface
    // or a `skipGraphqlErrorUi` operation stays a breadcrumb: that surface is already the report.
    // `UNAUTHENTICATED` is a breadcrumb too — an expired session is not an incident.
    const countable = errorClass === 'infrastructure' || errorClass === 'access_denied';
    const breadcrumbOnly =
      errorClass === 'auth' || ((inlineErrorUi || screenOwnsErrorUi) && !countable);
    const telemetry = {
      operation: operation.operationName,
      errorClass,
      code: code ?? null,
      httpStatus: response?.status,
    };

    if (breadcrumbOnly) {
      sentryReporter.addBreadcrumb({
        category: 'graphql',
        level: 'warning',
        message: `[graphql] ${operation.operationName} (${errorClass})`,
        data: {
          ...telemetry,
          graphQLErrors: graphQLErrors?.map(({ message }) => message).join(' ,'),
          networkError: networkError?.message,
        },
      });
    } else {
      sentryReporter.setContext('graphql', {
        errorClass,
        code: code ?? null,
        graphQLErrors,
        networkError,
        operation,
      });
      sentryReporter.captureMessage(`[graphql] ${operation.operationName}`, {
        level: 'warning',
        extra: {
          ...telemetry,
          graphQLErrors,
          networkError,
        },
      });
    }

    if (errorClass === 'auth') {
      const router = AppInjector.injector.get(Router);
      void authService.logOut();
      void router.navigate(['login'], {
        queryParams: {
          returnUrl: window.location,
        },
      });
      return;
    }

    // The screen renders this failure itself. Auth already ran above; the classes below would
    // stack a second surface on top of that screen.
    if (screenOwnsErrorUi) {
      return;
    }

    if (errorClass === 'infrastructure') {
      if (!accessBannerModalOpen) {
        accessBannerModalOpen = true;

        const modalV2Service = AppInjector.injector.get(ModalV2Service);

        const modalRef = modalV2Service.showModal({
          template: AccessBannerModalComponent,
          className: 'extra-wide',
        });

        modalRef.onHidden?.subscribe(() => (accessBannerModalOpen = false));
      }
      return;
    }

    if (errorClass === 'eula') {
      if (EULA_ALLOWLIST_OPERATIONS.includes(operation.operationName)) {
        return;
      }

      formLoadingStateService.setState(false);

      const eulaInterstitialService = AppInjector.injector.get(EulaInterstitialService);
      const eulaDocumentId = eulaDocumentIdFromErrors(collectedErrors(graphQLErrors, networkError));

      if (!eulaInterstitialModalOpen) {
        eulaInterstitialModalOpen = true;
        const modalV2Service = AppInjector.injector.get(ModalV2Service);
        const modalRef = modalV2Service.showModal({
          template: EulaInterstitialModalComponent,
          className: 'extra-wide',
          backdrop: 'static',
        });
        modalRef.onHidden?.subscribe(() => (eulaInterstitialModalOpen = false));
      }

      // Suppress toast; retry the original operation after accept (EC-304 / EC-308).
      // Apollo onError expects its own Observable, not an RxJS stream.
      return new Observable((observer) => {
        const sub = eulaInterstitialService.ensureAccepted(eulaDocumentId).subscribe({
          next: () => {
            forward(operation).subscribe(observer);
          },
          error: (err) => observer.error(err),
        });
        return () => sub.unsubscribe();
      });
    }

    if (errorClass === 'access_denied') {
      formLoadingStateService.setState(false);
      toastrService.error(ACCESS_DENIED_TEXT);
      return;
    }

    // An operation whose inline surface owns the error is kept off the toast because its inline
    // surface is already saying it — the same shape as the allowlisted operations.
    const toastSuppressed =
      inlineErrorUi || IGNORE_OPERATION_FAILURES.includes(operation.operationName);

    if (graphQLErrors) {
      formLoadingStateService.setState(false);

      const errors = graphQLErrors.map(({ message, locations, path }) => {
        console.log('[GraphQL error]: Message:', message);
        console.log('[GraphQL error]: Location:', locations);
        console.log('[GraphQL error]: Path:', path);
        return message;
      });
      if (!toastSuppressed) {
        toastrService.error(errors.join(' ,'));
      }
    }

    if (networkError) {
      if (!toastSuppressed) {
        toastrService.error(networkError.message);
      }
      console.debug('[Network error]', networkError);
    }

    return;
  });

const createApollo = (
  authService: CognitoAuthService,
  loaderService: LoaderService,
  customHeadersService: CustomHeadersService,
): ApolloClientOptions<any> => {
  const uri = environment.GRAPHQL_URL;

  const fetchWithLoader = async (input: string | URL | globalThis.Request, init?: RequestInit) => {
    loaderService.setLoader(true);

    return await fetch(input, init).finally(() => {
      loaderService.setLoader(false);
    });
  };

  const errorMiddleware = createErrorMiddleware(authService);

  const authMiddleware = setContext(async (_operation, { headers = {} }) => {
    // Fetch a fresh token on every request. Auth.currentSession() transparently
    // refreshes the id/access token via the Cognito refresh token when it has
    // expired, so we never send a stale/expired JWT (which would 401 -> logout).
    let token = authService.user.jwt ?? null;
    try {
      const session = await Auth.currentSession();
      token = session.getIdToken().getJwtToken();
      authService.user.jwt = token;
    } catch (e) {
      // No valid session (refresh token itself expired) — fall back to whatever
      // we have; the request will 401 and trigger logout in errorMiddleware.
      console.debug('[auth] currentSession failed', e);
    }

    return {
      headers: {
        ...headers,
        ...(token === null ? {} : { authorization: `Bearer ${token}` }),
      },
    };
  });

  const httpLink = createUploadLink({
    uri: (operation) => `${uri}?op=${operation.operationName}`,
    fetch: fetchWithLoader,
  });

  const customHeadersMiddleware = new ApolloLink((operation, forward) => {
    const customHeaders = customHeadersService.getCustomHeaders() || {};

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...customHeaders,
      },
    }));

    return forward(operation);
  });

  return {
    cache: createApolloCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
      mutate: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
    },
    link: ApolloLink.from([errorMiddleware, customHeadersMiddleware, authMiddleware, httpLink]),
  };
};

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [CognitoAuthService, LoaderService, CustomHeadersService],
    },
  ],
})
export class GraphQLModule {}
